import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import axios from "axios";
import { BASE_URL } from "../config/apiconfig";

const AddressContext = createContext(null);

export const useAddress = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error("useAddress must be used within AddressProvider");
  }
  return context;
};

export const AddressProvider = ({ children }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));

  // Enhanced token synchronization
  useEffect(() => {
    const handleStorage = () => {
      const newToken = localStorage.getItem("token");
      setToken(newToken);
    };

    // Listen for storage changes
    window.addEventListener("storage", handleStorage);

    // Also check for token changes periodically (for same-tab updates)
    const tokenCheckInterval = setInterval(() => {
      const currentToken = localStorage.getItem("token");
      if (currentToken !== token) {
        setToken(currentToken);
      }
    }, 1000);

    return () => {
      window.removeEventListener("storage", handleStorage);
      clearInterval(tokenCheckInterval);
    };
  }, [token]);

  // Clear addresses when token is removed (logout)
  useEffect(() => {
    if (!token) {
      setAddresses([]);
      setError(null);
    }
  }, [token]);

  // Fetch addresses with improved error handling
  const getAddresses = useCallback(
    async (userId) => {
      if (!userId || !token) {
        console.log('AddressContext: Cannot fetch addresses - missing userId or token', { userId: !!userId, token: !!token });
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('AddressContext: Fetching addresses for userId:', userId);

        const res = await axios.get(
          `${BASE_URL}/user-address/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log('AddressContext: Address fetch response:', res.data);

        if (res.data?.status === 200) {
          const fetchedAddresses = res.data.data || [];
          console.log('AddressContext: Setting addresses:', fetchedAddresses);
          setAddresses(fetchedAddresses);
          setError(null); // Clear any previous errors
        } else {
          console.error('AddressContext: Failed to fetch addresses - invalid response status');
          setError("Failed to fetch addresses");
          setAddresses([]); // Clear addresses on error
        }
      } catch (err) {
        console.error('AddressContext: Error fetching addresses:', err);
        const errorMessage = err.response?.data?.message || "Something went wrong";
        setError(errorMessage);

        // Clear addresses on error
        setAddresses([]);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // Add / Update address with improved error handling
  const saveAddress = useCallback(
    async (addressData, addressId = null) => {
      // Get userId from localStorage
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.id;

      if (!userId || !token) {
        console.error('AddressContext: Cannot save address - missing userId or token');
        return { success: false, message: "Unauthorized" };
      }

      try {
        const url = addressId
          ? `${BASE_URL}/user-address/${addressId}`
          : `${BASE_URL}/user-address`;

        const method = addressId ? "put" : "post";

        console.log('AddressContext: Saving address:', { url, method, addressData });

        const res = await axios({
          url,
          method,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            ...addressData,
            user_id: userId,
          },
        });

        console.log('AddressContext: Address save response:', res.data);

        // Refresh list after successful save
        await getAddresses(userId);

        return { success: true, data: res.data };
      } catch (err) {
        console.error('AddressContext: Error saving address:', err);
        return {
          success: false,
          message: err.response?.data?.message || "Save failed",
        };
      }
    },
    [token, getAddresses]
  );

  // Set primary address with improved error handling
  const setPrimaryAddress = useCallback(
    async (userId, addressId) => {
      if (!userId || !token) {
        console.error('AddressContext: Cannot set primary address - missing userId or token');
        return { success: false, message: "Unauthorized" };
      }

      try {
        console.log('AddressContext: Setting primary address:', { userId, addressId });

        const res = await axios.put(
          `${BASE_URL}/user-address/${addressId}`,
          {
            is_primary: "1",
            update_audit_id: userId
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log('AddressContext: Set primary address response:', res.data);

        // Refresh addresses after marking primary
        await getAddresses(userId);

        return { success: true, data: res.data };
      } catch (err) {
        console.error("AddressContext: Set primary address error:", err);
        return {
          success: false,
          message: err.response?.data?.message || "Failed to set primary",
        };
      }
    },
    [token, getAddresses]
  );

  // Delete address with improved error handling
  const deleteAddress = useCallback(
    async (userId, addressId) => {
      if (!userId || !token) {
        console.error('AddressContext: Cannot delete address - missing userId or token');
        return { success: false, message: "Unauthorized" };
      }

      try {
        console.log('AddressContext: Deleting address:', { userId, addressId });

        await axios.delete(`${BASE_URL}/user-address/${addressId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            delete_audit_id: userId
          },
        });

        console.log('AddressContext: Address deleted successfully');

        // Refresh addresses after deletion
        await getAddresses(userId);

        return { success: true };
      } catch (err) {
        console.error("AddressContext: Delete address error:", err);

        return {
          success: false,
          message: err.response?.data?.message || "Delete failed",
        };
      }
    },
    [token, getAddresses]
  );

  return (
    <AddressContext.Provider
      value={{
        addresses,
        loading,
        error,
        getAddresses,
        saveAddress,
        deleteAddress,
        setAddresses,
        setPrimaryAddress,
      }}
    >
      {children}
    </AddressContext.Provider>
  );
};
